import { mulberry32 } from '@puzzle-arena/shared';
export const MINE = -1;
export const DIFFICULTY_CONFIGS = {
    easy: { rows: 9, cols: 9, totalMines: 10 },
    medium: { rows: 16, cols: 16, totalMines: 40 },
    hard: { rows: 16, cols: 30, totalMines: 99 },
    expert: { rows: 20, cols: 30, totalMines: 130 },
};
/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */
export function getNeighbors(row, col, rows, cols) {
    const neighbors = [];
    for (let dr = -1; dr <= 1; dr++) {
        for (let dc = -1; dc <= 1; dc++) {
            if (dr === 0 && dc === 0)
                continue;
            const nr = row + dr;
            const nc = col + dc;
            if (nr >= 0 && nr < rows && nc >= 0 && nc < cols) {
                neighbors.push({ r: nr, c: nc });
            }
        }
    }
    return neighbors;
}
/* ------------------------------------------------------------------ */
/* Generator                                                          */
/* ------------------------------------------------------------------ */
export function generate(opts) {
    const t0 = Date.now();
    const seed = opts.seed ?? 42;
    const difficulty = opts.difficulty ?? 'medium';
    const cfg = DIFFICULTY_CONFIGS[difficulty];
    const { rows, cols, totalMines } = cfg;
    const rng = mulberry32(seed);
    const startR = Math.floor(rows / 2);
    const startC = Math.floor(cols / 2);
    // Forbidden cells for mines: safe start and its 8 neighbors
    const forbidden = new Set();
    forbidden.add(startR * cols + startC);
    for (const n of getNeighbors(startR, startC, rows, cols)) {
        forbidden.add(n.r * cols + n.c);
    }
    // Eligible indices for mine placement
    const totalCells = rows * cols;
    const eligible = [];
    for (let i = 0; i < totalCells; i++) {
        if (!forbidden.has(i))
            eligible.push(i);
    }
    // Fisher-Yates shuffle eligible cells to place mines
    for (let i = eligible.length - 1; i > 0; i--) {
        const j = rng.int(i + 1);
        const tmp = eligible[i];
        eligible[i] = eligible[j];
        eligible[j] = tmp;
    }
    const mineIndices = new Set(eligible.slice(0, totalMines));
    const grid = new Array(totalCells).fill(0);
    for (const idx of mineIndices) {
        grid[idx] = MINE;
    }
    // Compute adjacent counts
    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
            const idx = r * cols + c;
            if (grid[idx] === MINE)
                continue;
            let count = 0;
            for (const n of getNeighbors(r, c, rows, cols)) {
                if (grid[n.r * cols + n.c] === MINE)
                    count++;
            }
            grid[idx] = count;
        }
    }
    return {
        puzzle: {
            rows,
            cols,
            totalMines,
            safeStart: { row: startR, col: startC },
            seed,
            difficulty,
        },
        solution: {
            rows,
            cols,
            totalMines,
            grid,
        },
        meta: {
            actualDifficulty: difficulty,
            generationMs: Date.now() - t0,
            seed,
        },
    };
}
/* ------------------------------------------------------------------ */
/* Flood Fill Reveal                                                  */
/* ------------------------------------------------------------------ */
export function revealCell(solution, revealed, row, col) {
    const { rows, cols, grid } = solution;
    const targetIdx = row * cols + col;
    const nextRevealed = [...revealed];
    if (targetIdx < 0 || targetIdx >= rows * cols) {
        return { revealed: nextRevealed, detonated: false, countRevealed: 0 };
    }
    if (grid[targetIdx] === MINE) {
        nextRevealed[targetIdx] = true;
        return { revealed: nextRevealed, detonated: true, countRevealed: 1 };
    }
    if (nextRevealed[targetIdx]) {
        return { revealed: nextRevealed, detonated: false, countRevealed: 0 };
    }
    let count = 0;
    const queue = [{ r: row, c: col }];
    nextRevealed[targetIdx] = true;
    count++;
    while (queue.length > 0) {
        const curr = queue.shift();
        const currIdx = curr.r * cols + curr.c;
        const val = grid[currIdx];
        // If it's a 0, cascade to all unrevealed neighbors
        if (val === 0) {
            for (const n of getNeighbors(curr.r, curr.c, rows, cols)) {
                const nIdx = n.r * cols + n.c;
                if (!nextRevealed[nIdx] && grid[nIdx] !== MINE) {
                    nextRevealed[nIdx] = true;
                    count++;
                    if (grid[nIdx] === 0) {
                        queue.push({ r: n.r, c: n.c });
                    }
                }
            }
        }
    }
    return { revealed: nextRevealed, detonated: false, countRevealed: count };
}
/* ------------------------------------------------------------------ */
/* Solve Path / Order for Bots & Solvers                              */
/* ------------------------------------------------------------------ */
export function solveOrder(puzzle, solution) {
    const { rows, cols, grid } = solution;
    const order = [];
    const visited = new Array(rows * cols).fill(false);
    // Safe start first
    const startIdx = puzzle.safeStart.row * cols + puzzle.safeStart.col;
    order.push(`${puzzle.safeStart.row},${puzzle.safeStart.col}`);
    visited[startIdx] = true;
    // BFS from safe start
    const queue = [{ r: puzzle.safeStart.row, c: puzzle.safeStart.col }];
    while (queue.length > 0) {
        const curr = queue.shift();
        for (const n of getNeighbors(curr.r, curr.c, rows, cols)) {
            const idx = n.r * cols + n.c;
            if (!visited[idx] && grid[idx] !== MINE) {
                visited[idx] = true;
                order.push(`${n.r},${n.c}`);
                queue.push({ r: n.r, c: n.c });
            }
        }
    }
    // Any remaining non-mine cells
    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
            const idx = r * cols + c;
            if (!visited[idx] && grid[idx] !== MINE) {
                visited[idx] = true;
                order.push(`${r},${c}`);
            }
        }
    }
    return order;
}
/* ------------------------------------------------------------------ */
/* Grading                                                            */
/* ------------------------------------------------------------------ */
export function grade(playerState, solution) {
    const { rows, cols, totalMines, grid } = solution;
    const totalNonMines = rows * cols - totalMines;
    let revealed = [];
    let detonated = false;
    if (playerState && typeof playerState === 'object') {
        const ps = playerState;
        if (Array.isArray(ps.revealed))
            revealed = ps.revealed;
        if (typeof ps.detonated === 'boolean')
            detonated = ps.detonated;
    }
    let revealedNonMines = 0;
    for (let i = 0; i < grid.length; i++) {
        if (grid[i] !== MINE && revealed[i]) {
            revealedNonMines++;
        }
    }
    const complete = !detonated && revealedNonMines >= totalNonMines;
    const cellsFilled = revealed.filter(Boolean).length;
    const cellsCorrect = revealedNonMines;
    return {
        cellsFilled,
        cellsCorrect,
        cellsTotal: totalNonMines,
        complete,
    };
}
/* ------------------------------------------------------------------ */
/* Hint                                                               */
/* ------------------------------------------------------------------ */
export function hint(solution, playerState, rng) {
    const { rows, cols, grid } = solution;
    let revealed = [];
    if (playerState && typeof playerState === 'object' && Array.isArray(playerState.revealed)) {
        revealed = playerState.revealed;
    }
    const unrevealedSafe = [];
    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
            const idx = r * cols + c;
            if (!revealed[idx] && grid[idx] !== MINE) {
                unrevealedSafe.push({ r, c });
            }
        }
    }
    if (unrevealedSafe.length === 0)
        return null;
    const pick = unrevealedSafe[rng.int(unrevealedSafe.length)];
    return { path: `${pick.r},${pick.c}`, value: 1 };
}
//# sourceMappingURL=minesweeper.js.map